import type { IChartData, ISearchTermsGrowth } from "../../dashboard/types";
import type { IHistoricoDePosicoes, IPalavraComHistorico } from "../types";
import { mesCurto } from "./formatar";

export type PontoDatado = { posicao: number; quando: string; data: string | null };

export type ComparacaoDePosicao = {
  id: number;
  titulo: string;
  antes: PontoDatado;
  depois: PontoDatado;
  delta: number;
  situacao: "caiu" | "manteve" | "subiu";
};

/** Posição 0 = medida e fora da lista: pior que qualquer posição numerada. */
export const foraDaLista = (posicao: number): boolean => posicao <= 0;

const ordem = (posicao: number): number => (foraDaLista(posicao) ? Number.POSITIVE_INFINITY : posicao);

const pontosMedidos = (palavra: IPalavraComHistorico): PontoDatado[] =>
  palavra.byMonth
    .filter((mes) => mes.position !== null)
    .map((mes) => ({ posicao: mes.position as number, quando: mes.yearMonth, data: mes.recordedAt }));

/**
 * Antes × depois por palavra: a primeira posição conquistada (> 0) da janela
 * contra a última medição, que pode ser "fora da lista" (0). Só entram
 * palavras com medições em meses diferentes — uma medição isolada não
 * sustenta "caiu de 2º para 8º".
 */
export const comparacoesDePosicao = (
  historico: IHistoricoDePosicoes | null | undefined,
  limite = 3,
): ComparacaoDePosicao[] => {
  if (!historico) return [];
  const comparacoes: ComparacaoDePosicao[] = [];
  historico.keywords
    .filter((palavra) => !palavra.extra)
    .forEach((palavra) => {
      const pontos = pontosMedidos(palavra);
      const antes = pontos.find((ponto) => !foraDaLista(ponto.posicao));
      const depois = pontos[pontos.length - 1];
      if (!antes || !depois || antes.quando === depois.quando) return;
      const delta = foraDaLista(depois.posicao)
        ? Number.POSITIVE_INFINITY
        : ordem(depois.posicao) - ordem(antes.posicao);
      comparacoes.push({
        id: palavra.keyword.id,
        titulo: palavra.keyword.title,
        antes,
        depois,
        delta,
        situacao: delta > 0 ? "caiu" : delta < 0 ? "subiu" : "manteve",
      });
    });
  return comparacoes
    .sort((a, b) => b.delta - a.delta || a.antes.posicao - b.antes.posicao)
    .slice(0, limite);
};

/** A palavra que mais subiu entre a primeira e a última medição válida da janela. */
export const maiorAvanco = (historico: IHistoricoDePosicoes | null | undefined): ComparacaoDePosicao | null =>
  comparacoesDePosicao(historico, Number.MAX_SAFE_INTEGER)
    .filter((comparacao) => comparacao.situacao === "subiu")
    .sort((a, b) => a.delta - b.delta)[0] ?? null;

export type ComparacaoMensal = {
  antes: { valor: number; mes: string };
  depois: { valor: number; mes: string };
  variacao: number | null;
};

const variacao = (antes: number, depois: number): number | null =>
  antes > 0 ? Math.round(((depois - antes) / antes) * 100) : null;

const JANELA = 3;

const media = (valores: number[]): number =>
  valores.length ? Math.round(valores.reduce((soma, valor) => soma + valor, 0) / valores.length) : 0;

const rotuloDaJanela = (meses: string[]): string => {
  if (meses.length === 1) return mesCurto(meses[0]);
  const [primeiro, ultimo] = [mesCurto(meses[0]), mesCurto(meses[meses.length - 1])];
  return `média ${primeiro.split("/")[0]}–${ultimo}`;
};

/** Média zerada num dos lados é falta de dado do Google (conexão perdida), não uma queda a 100%. */
const comparar = (
  mesesAntes: string[],
  valoresAntes: number[],
  mesesDepois: string[],
  valoresDepois: number[],
): ComparacaoMensal | null => {
  const antes = { valor: media(valoresAntes), mes: rotuloDaJanela(mesesAntes) };
  const depois = { valor: media(valoresDepois), mes: rotuloDaJanela(mesesDepois) };
  if (antes.valor <= 0 || depois.valor <= 0) return null;
  return { antes, depois, variacao: variacao(antes.valor, depois.valor) };
};

/**
 * Pesquisas que mostraram a empresa: média dos 3 primeiros meses da janela
 * contra a média dos 3 últimos. Média, e não o melhor mês, para não escolher
 * um pico a dedo — um mês sozinho não sustenta "caiu".
 */
export const pesquisasAntesDepois = (dados: ISearchTermsGrowth | null | undefined): ComparacaoMensal | null => {
  if (!dados || dados.months.length < 2 * JANELA) return null;
  const n = dados.months.length;
  return comparar(
    dados.months.slice(0, JANELA),
    dados.totalByMonth.slice(0, JANELA),
    dados.months.slice(n - JANELA),
    dados.totalByMonth.slice(n - JANELA),
  );
};

const chaveDoMes = (data: Date) => `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}`;

/** Ligações registradas pelo Google: média de 3 meses fechados de meio ano atrás contra os 3 últimos fechados. */
export const ligacoesAntesDepois = (
  chartData: IChartData | null | undefined,
  hoje: Date = new Date(),
): ComparacaoMensal | null => {
  if (!chartData?.clicksChart?.length) return null;
  const ler = (data: Date): number => {
    const ano = chartData.clicksChart.find((item) => item.year === data.getFullYear());
    if (!ano) return 0;
    const chave = String(data.getMonth() + 1).padStart(2, "0") as keyof typeof ano.categories.CALLS;
    return ano.categories.CALLS?.[chave] ?? 0;
  };
  const meses = (inicio: number) =>
    Array.from({ length: JANELA }, (_, i) => new Date(hoje.getFullYear(), hoje.getMonth() - inicio + i, 1));
  const antes = meses(JANELA * 2 + 2);
  const depois = meses(JANELA);
  return comparar(antes.map(chaveDoMes), antes.map(ler), depois.map(chaveDoMes), depois.map(ler));
};

/** Volume no lugar de dinheiro: quantas pesquisas por mês deixaram de mostrar a empresa. */
export const pesquisasAMenosPorMes = (comparacao: ComparacaoMensal | null): number | null => {
  if (!comparacao) return null;
  const diferenca = comparacao.antes.valor - comparacao.depois.valor;
  return diferenca > 0 ? diferenca : null;
};
